import React, { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { tokens } from '../../../theme/tokens';
import { toAbsoluteImageUrl } from '../../../utils/productDetailHelpers';
import type { CompatibilityGroupKey, CompatibilityItem, ProductCompatibility } from '../ProductWorksWith';
import { Dot, TextLink, Thumb } from './primitives';

const GROUPS: Array<{ key: CompatibilityGroupKey; label: string }> = [
  { key: 'lenses', label: 'lensCompatibility.lenses' },
  { key: 'cameras', label: 'lensCompatibility.cameras' },
  { key: 'accessories', label: 'lensCompatibility.accessories' },
];

const COLLAPSED = 6;
const PAGE = 60;

export interface DealerCompatibilityTabProps {
  productId: string;
  compatibility: ProductCompatibility;
  syncSelectedIds: Set<string>;
  canAddToSync: boolean;
  onAddToSync: (item: CompatibilityItem) => void;
  addingIds: Set<string>;
  onOpenProduct: (item: CompatibilityItem) => void;
}

/** Counts per group for the Works with button and the tab badge. */
export function compatibilityCounts(c: ProductCompatibility | null | undefined, label: (key: string) => string) {
  if (!c) return null;
  return GROUPS.map((g) => ({ key: g.key, label: label(g.label), count: (c[g.key] ?? []).length }));
}

export function DealerCompatibilityTab(props: DealerCompatibilityTabProps) {
  const { t } = useAppTranslation();
  const [filter, setFilter] = useState<'all' | CompatibilityGroupKey>('all');
  const groups = GROUPS.map((g) => ({ ...g, items: props.compatibility[g.key] ?? [], more: !!props.compatibility.more?.[g.key] })).filter((g) => g.items.length > 0);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const shown = groups.filter((g) => filter === 'all' || g.key === filter);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', p: { xs: '16px', sm: '22px' }, bgcolor: tokens.surface.subtle }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Typography component="span" sx={{ fontSize: '15px', fontWeight: 600 }}>Works with</Typography>
          <Typography component="span" sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>
            {total === 0 ? t('lensCompatibility.empty') : `${total}${groups.some((g) => g.more) ? '+' : ''} catalog links`}
          </Typography>
        </Box>
        {groups.length > 1 && (
          <Box role="tablist" sx={{ display: 'flex', alignItems: 'center', gap: '2px', p: '3px', borderRadius: '10px', bgcolor: tokens.surface.tint, flexWrap: 'wrap' }}>
            {[{ key: 'all' as const, label: 'All', count: total }, ...groups.map((g) => ({ key: g.key, label: t(g.label), count: g.items.length }))].map((f) => {
              const on = filter === f.key;
              return (
                <ButtonBase
                  key={f.key}
                  role="tab"
                  aria-selected={on}
                  onClick={() => setFilter(f.key)}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 28, px: '11px', borderRadius: '8px', bgcolor: on ? tokens.surface.card : 'transparent', boxShadow: on ? '0 1px 2px rgba(15,23,42,0.08)' : 'none', color: on ? tokens.ink.primary : tokens.ink.muted, fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  <span>{f.label}</span>
                  <Box component="span" sx={{ color: tokens.ink.faint }}>{f.count}</Box>
                </ButtonBase>
              );
            })}
          </Box>
        )}
      </Box>

      {total === 0 ? (
        <Typography sx={{ fontSize: '13px', color: tokens.ink.muted }}>{t('lensCompatibility.emptyHint')}</Typography>
      ) : (
        shown.map((g) => <CompatGroup key={g.key} group={g.key} label={t(g.label)} items={g.items} hasMore={g.more} {...props} />)
      )}

      <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>
        Links come from the Motzu catalog. Spot one that's wrong? Use Report issue at the top.
      </Typography>
    </Box>
  );
}

function CompatGroup({
  group,
  label,
  items,
  hasMore,
  productId,
  syncSelectedIds,
  canAddToSync,
  onAddToSync,
  addingIds,
  onOpenProduct,
}: DealerCompatibilityTabProps & { group: CompatibilityGroupKey; label: string; items: CompatibilityItem[]; hasMore: boolean }) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const loadsFullList = hasMore || items.length > COLLAPSED;
  const full = useInfiniteQuery({
    queryKey: ['product-compatibility', productId, group],
    queryFn: ({ pageParam }) => apiService.getProductCompatibilityPage(productId, group, pageParam, PAGE),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    enabled: open && hasMore,
  });
  const fullItems = (full.data?.pages.flatMap((p) => p.items) ?? []) as CompatibilityItem[];
  const totalFull = full.data?.pages[0]?.total;
  const list = open ? (hasMore && fullItems.length ? fullItems : items) : items.slice(0, COLLAPSED);
  const countLabel = totalFull ?? (hasMore ? `${items.length}+` : items.length);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
        <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.ink.muted }}>{label}</Typography>
        <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{countLabel}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))' }, gap: '10px' }}>
        {list.map((c) => {
          const inStore = syncSelectedIds.has(c.id);
          const adding = addingIds.has(c.id);
          const native = c.via === 'native' || c.via === 'manual';
          return (
            <Box key={c.id} sx={{ display: 'grid', gridTemplateColumns: { xs: 'auto minmax(0,1fr) auto', sm: '40px minmax(0,1fr) auto' }, gap: '12px', alignItems: 'center', px: '12px', py: '10px', bgcolor: tokens.surface.card, border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px' }}>
              <ButtonBase onClick={() => onOpenProduct(c)} aria-label={t('lensCompatibility.open', { name: c.name })} sx={{ borderRadius: '8px' }}>
                <Thumb src={c.image ? toAbsoluteImageUrl(c.image) || c.image : null} size={40} radius={8} />
              </ButtonBase>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                <ButtonBase onClick={() => onOpenProduct(c)} sx={{ justifyContent: 'flex-start', textAlign: 'left', minWidth: 0 }}>
                  <Typography component="span" sx={{ fontSize: '13.5px', fontWeight: 500, color: tokens.ink.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</Typography>
                </ButtonBase>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 19, px: '7px', borderRadius: '5px', bgcolor: native ? tokens.signal.positive.tint : tokens.accent.tint, color: native ? tokens.signal.positive.ink : tokens.accent.hover, fontSize: '11px', fontWeight: 600 }}>
                    {t(`lensCompatibility.via.${c.via}`)}
                  </Box>
                  <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{[c.brand, c.mount].filter(Boolean).join(' · ')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {inStore ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: tokens.signal.positive.ink, whiteSpace: 'nowrap' }}>
                    <Dot color={tokens.signal.positive.solid} />In your store
                  </Box>
                ) : canAddToSync ? (
                  <ButtonBase
                    onClick={() => onAddToSync(c)}
                    disabled={adding}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: 28, px: '10px', border: '1px solid', borderColor: tokens.line.default, borderRadius: '8px', bgcolor: tokens.surface.card, color: tokens.accent.main, fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { borderColor: tokens.accent.border, bgcolor: tokens.signal.info.row } }}
                  >
                    {adding && <CircularProgress size={10} />}
                    + Add to sync
                  </ButtonBase>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
      {full.isError && <Typography sx={{ fontSize: '12.5px', color: tokens.signal.caution.ink }}>{t('lensCompatibility.loadError')}</Typography>}
      {loadsFullList && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <TextLink onClick={() => setOpen((v) => !v)}>
            {open ? t('lensCompatibility.showFewer') : hasMore ? t('lensCompatibility.showAllMore') : t('lensCompatibility.showAll', { count: items.length })}
          </TextLink>
          {open && full.hasNextPage && (
            <TextLink onClick={() => full.fetchNextPage()} disabled={full.isFetchingNextPage}>{t('lensCompatibility.loadMore')}</TextLink>
          )}
          {open && full.isFetching && <CircularProgress size={12} />}
          {open && totalFull != null && (
            <Typography component="span" sx={{ fontSize: '12px', color: tokens.ink.faint }}>{t('lensCompatibility.showingOf', { shown: fullItems.length, total: totalFull })}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
